/**
 * browserCompatibility.ts
 * 
 * Browser feature detection and cross-browser compatibility utilities for drag-drop.
 * 
 * Features:
 * - Feature detection for webkitGetAsEntry and FileSystemEntry APIs
 * - Browser version detection
 * - Drag-drop capability detection
 * - Graceful fallback handling
 */

/**
 * Browser detection info
 */
export interface BrowserInfo {
  name: 'Chrome' | 'Firefox' | 'Safari' | 'Edge' | 'Unknown';
  version: number | null;
  majorVersion: number | null;
}

/**
 * Drag-drop capabilities
 */
export interface DragDropCapabilities {
  supportsDragDrop: boolean;
  supportsFolderDrops: boolean;
  supportsWebkitGetAsEntry: boolean;
  supportsDataTransferItems: boolean;
  supportsFileSystemEntry: boolean;
  recommendedFallback: 'filepicker' | 'manual' | 'none';
  browserInfo: BrowserInfo;
}

/**
 * Parse browser user agent string to get browser name and version
 */
const parseBrowserInfo = (): BrowserInfo => {
  // Only run in browser context
  if (typeof navigator === 'undefined') {
    return { name: 'Unknown', version: null, majorVersion: null };
  }
  
  const ua = navigator.userAgent;
  let name: BrowserInfo['name'] = 'Unknown';
  let version: number | null = null;
  let majorVersion: number | null = null;
  
  // Edge detection (must be before Chrome as Edge also contains 'Chrome')
  if (ua.includes('Edg/')) {
    name = 'Edge';
    const match = ua.match(/Edg[e\/](\d+)/);
    if (match) {
      majorVersion = parseInt(match[1], 10);
      version = majorVersion;
    }
  }
  // Chrome detection
  else if (ua.includes('Chrome/')) {
    name = 'Chrome';
    const match = ua.match(/Chrome\/(\d+)/);
    if (match) {
      majorVersion = parseInt(match[1], 10);
      version = majorVersion;
    }
  }
  // Firefox detection
  else if (ua.includes('Firefox/')) {
    name = 'Firefox';
    const match = ua.match(/Firefox\/(\d+)/);
    if (match) {
      majorVersion = parseInt(match[1], 10);
      version = majorVersion;
    }
  }
  // Safari detection (must be after Chrome since some Safari builds contain 'Chrome')
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) {
    name = 'Safari';
    const match = ua.match(/Version\/(\d+)/);
    if (match) {
      majorVersion = parseInt(match[1], 10);
      version = majorVersion;
    }
  }
  
  return { name, version, majorVersion };
};

/**
 * Check if browser supports webkitGetAsEntry API
 */
const supportsWebkitGetAsEntry = (): boolean => {
  try {
    // Feature detection: check if DataTransferItem has webkitGetAsEntry
    return (
      typeof DataTransferItem !== 'undefined' &&
      typeof DataTransferItem.prototype.webkitGetAsEntry === 'function'
    );
  } catch {
    return false;
  }
};

/**
 * Check if browser supports FileSystemEntry APIs
 */
const supportsFileSystemEntry = (): boolean => {
  try {
    return (
      typeof FileSystemEntry !== 'undefined' &&
      typeof FileSystemFileEntry !== 'undefined' &&
      typeof FileSystemDirectoryEntry !== 'undefined' &&
      typeof FileSystemDirectoryReader !== 'undefined'
    );
  } catch {
    return false;
  }
};

/**
 * Check if browser supports DataTransferItem API
 */
const supportsDataTransferItems = (): boolean => {
  try {
    return typeof DataTransferItem !== 'undefined';
  } catch {
    return false;
  }
};

/**
 * Check if basic drag-drop is supported
 */
const supportsDragDrop = (): boolean => {
  // All modern browsers support basic drag-drop events
  try {
    return (
      typeof DragEvent !== 'undefined' &&
      typeof DataTransfer !== 'undefined'
    );
  } catch {
    return false;
  }
};

/**
 * Get recommended fallback based on browser capabilities
 */
const getRecommendedFallback = (
  browserInfo: BrowserInfo,
  supportsFolders: boolean
): 'filepicker' | 'manual' | 'none' => {
  // If folders are supported, no fallback needed
  if (supportsFolders) {
    return 'none';
  }
  
  // For older browsers or those without folder support, recommend file picker
  if (
    browserInfo.name === 'Unknown' ||
    (browserInfo.majorVersion && browserInfo.majorVersion < 88)
  ) {
    return 'filepicker';
  }
  
  // For modern browsers, file picker as fallback
  return 'filepicker';
};

/**
 * Get complete drag-drop capabilities for the current browser
 */
export const getDragDropCapabilities = (): DragDropCapabilities => {
  const browserInfo = parseBrowserInfo();
  
  const supportsDragDropBasic = supportsDragDrop();
  const supportsDataTransfer = supportsDataTransferItems();
  const supportsWebkit = supportsWebkitGetAsEntry();
  const supportsFS = supportsFileSystemEntry();
  
  const supportsFolderDrops = supportsDragDropBasic && supportsWebkit && supportsFS;
  
  return {
    supportsDragDrop: supportsDragDropBasic,
    supportsFolderDrops,
    supportsWebkitGetAsEntry: supportsWebkit,
    supportsDataTransferItems: supportsDataTransfer,
    supportsFileSystemEntry: supportsFS,
    recommendedFallback: getRecommendedFallback(browserInfo, supportsFolderDrops),
    browserInfo,
  };
};

/**
 * Check if a specific browser version meets minimum requirements
 */
export const meetsMinimumBrowserVersion = (): boolean => {
  const caps = getDragDropCapabilities();
  const { browserInfo } = caps;
  
  if (!browserInfo.majorVersion) {
    return false; // Unknown browser, assume doesn't meet requirements
  }
  
  switch (browserInfo.name) {
    case 'Chrome':
      return browserInfo.majorVersion >= 90;
    case 'Firefox':
      return browserInfo.majorVersion >= 88;
    case 'Safari':
      return browserInfo.majorVersion >= 14;
    case 'Edge':
      return browserInfo.majorVersion >= 90;
    default:
      return false;
  }
};

/**
 * Get browser name and version string for logging/debugging
 */
export const getBrowserString = (): string => {
  const caps = getDragDropCapabilities();
  const { browserInfo } = caps;
  
  if (browserInfo.version) {
    return `${browserInfo.name} ${browserInfo.version}`;
  }
  
  return browserInfo.name;
};

/**
 * Create a console message about drag-drop capabilities
 */
export const logDragDropCapabilities = (): void => {
  const caps = getDragDropCapabilities();
  
  console.info('Drag-drop Capabilities:', {
    browser: getBrowserString(),
    dragDrop: caps.supportsDragDrop,
    folderDrops: caps.supportsFolderDrops,
    webkitGetAsEntry: caps.supportsWebkitGetAsEntry,
    dataTransferItems: caps.supportsDataTransferItems,
    fileSystemEntry: caps.supportsFileSystemEntry,
    recommendedFallback: caps.recommendedFallback,
    meetsPlatformRequirements: meetsMinimumBrowserVersion(),
  });
};

/**
 * Check if the current browser is in the support matrix
 */
export const isSupportedBrowser = (): boolean => {
  return meetsMinimumBrowserVersion();
};

export default getDragDropCapabilities;
